import { useId, useRef, useState, type KeyboardEvent } from "react";
import { CircleHelp, Heart, MessageCircle, Target, TrendingUp, Users, type LucideIcon } from "lucide-react";
import CanonicalTags from "@/components/seo/CanonicalTags";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { CheckList, CtaBand, SectionHead, SignUpCta } from "@/components/marketing/parts";
import { crossfade } from "@/components/marketing/motion";

// The Framework's five panels. Keys are stable; culture is the default. The
// source testimonials and "What Compaire Surfaces" lists are intentionally not
// shown (unsubstantiated; see the v3.3 review's Claims D).
const PILLARS: {
  key: string;
  title: string;
  Icon: LucideIcon;
  body: string;
  checks: string[];
  questions: string[];
}[] = [
  {
    key: "culture",
    title: "Intentional Culture",
    Icon: Heart,
    body: "Culture isn't a ping-pong table. It's how a team shows up — for each other, for clients, and for the mission.",
    checks: [
      "Defined team culture and shared values",
      "Team routines that help people feel included",
      "Agents who actively choose to be there",
    ],
    questions: [
      "How does the team describe its culture?",
      "What do agents say about the day-to-day experience?",
    ],
  },
  {
    key: "leadership",
    title: "Real Leadership",
    Icon: Users,
    body: "The difference between a broker and a leader is whether they know your name and your goals.",
    checks: ["Coaches, not just manages", "Accessible and genuinely invested", "Clear opportunities for agents to learn and grow"],
    questions: ["How accessible are leaders?", "What coaching and development support is available?"],
  },
  {
    key: "communication",
    title: "Communication",
    Icon: MessageCircle,
    body: "Great teams communicate about wins, losses, expectations, and the hard stuff — not just commission splits.",
    checks: [
      "Clear expectations and real feedback",
      "Regular team conversations",
      "Transparent about business direction",
    ],
    questions: ["How are expectations communicated?", "How does the team give and receive feedback?"],
  },
  {
    key: "productivity",
    title: "Productivity",
    Icon: TrendingUp,
    body: "Look for tools and support that help you build your business.",
    checks: [
      "Systems and tools that work for you",
      "Support that makes everyday work easier",
      "A team setup that fits how you work",
    ],
    questions: ["What tools and support are available?", "How does the team's structure fit the way you work?"],
  },
  {
    key: "values",
    title: "Shared Values",
    Icon: Target,
    body: "Look for a team whose values match your own, from client service to the way people work together.",
    checks: [
      "A shared approach to serving clients",
      "Ethics and professionalism standards",
      "Community and market focus",
    ],
    questions: ["How does the team approach client service?", "Which professional values guide its decisions?"],
  },
];

const PROBLEMS = [
  {
    title: "What does everyday work feel like?",
    body: "Look for how people work together, support one another and treat clients. Those details help you picture yourself there.",
  },
  {
    title: "Who will help you grow?",
    body: "Ask about coaching, access to leaders and support when a deal gets difficult.",
  },
  {
    title: "Does it reflect what matters to you?",
    body: "Compare expectations, client service and professional standards with the way you want to build your business.",
  },
];

const STEPS = [
  {
    title: "Tell Us What Matters",
    body: "Culture preferences, leadership style, values, team size, growth goals. Create your profile.",
  },
  {
    title: "Get Your Matches",
    body: "Review teams using your stated preferences, available organization information, and review ratings. All anonymous until you're ready.",
  },
  {
    title: "Connect & Decide",
    body: "Book calls with your top matches. Make your move with confidence, knowing you found the right fit.",
  },
];

/**
 * ARIA tabs: roving tabindex, ← → Home End, the panel labelled by its tab.
 * Every panel is rendered (inactive ones hidden) so the prerendered page
 * carries all five for crawlers.
 */
const Framework = () => {
  const [active, setActive] = useState(0);
  const uid = useId();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const panelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tabId = (key: string) => `${uid}-tab-${key}`;
  const panelId = (key: string) => `${uid}-panel-${key}`;

  const select = (index: number, focus: boolean) => {
    setActive(index);
    if (focus) tabRefs.current[index]?.focus();
    requestAnimationFrame(() => crossfade(panelRefs.current[index]));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const n = PILLARS.length;
    const next =
      event.key === "ArrowRight"
        ? (active + 1) % n
        : event.key === "ArrowLeft"
          ? (active - 1 + n) % n
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? n - 1
              : null;
    if (next === null) return;
    event.preventDefault();
    select(next, true);
  };

  return (
    <section className="mk-section mk-section--white mk-section--rule">
      <div className="mk-container mk-stack" style={{ gap: "clamp(36px,4cqi,56px)" }}>
        <SectionHead
          label="What to look for"
          title="What you really need in your next team."
          body="Five things to consider when choosing a team or brokerage."
        />
        <div role="tablist" aria-label="What to look for" className="mk-tabs" onKeyDown={onKeyDown}>
          {PILLARS.map((p, i) => (
            <button
              key={p.key}
              ref={(el) => (tabRefs.current[i] = el)}
              type="button"
              role="tab"
              id={tabId(p.key)}
              aria-controls={panelId(p.key)}
              aria-selected={active === i}
              tabIndex={active === i ? 0 : -1}
              className="mk-tab"
              onClick={() => select(i, false)}
            >
              <p.Icon size={18} aria-hidden="true" />
              {p.title}
            </button>
          ))}
        </div>
        {PILLARS.map((p, i) => (
          <div
            key={p.key}
            ref={(el) => (panelRefs.current[i] = el)}
            role="tabpanel"
            id={panelId(p.key)}
            aria-labelledby={tabId(p.key)}
            tabIndex={0}
            hidden={active !== i}
            style={{
              display: active === i ? "grid" : "none",
              gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,420px),1fr))",
              gap: "clamp(32px,5cqi,72px)",
              outlineOffset: 6,
            }}
          >
            <div className="mk-stack" style={{ gap: 20 }}>
              <h3
                style={{
                  font: "600 var(--mk-text-h2-sm)/1.15 var(--mk-sans)",
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                }}
              >
                <p.Icon size={28} color="var(--mk-primary)" aria-hidden="true" style={{ flex: "none" }} />
                {p.title}
              </h3>
              <p className="mk-body" style={{ maxWidth: 520, textWrap: "pretty" }}>
                {p.body}
              </p>
              <CheckList items={p.checks} size={18} style={{ gap: 12, font: "500 var(--mk-text-label-sm)/1.3 var(--mk-sans)" }} />
            </div>
            <div
              className="mk-stack"
              style={{
                alignSelf: "start",
                background: "var(--mk-paper)",
                border: "1px solid var(--mk-border)",
                borderRadius: 8,
                padding: "clamp(24px,3cqi,36px)",
                gap: 18,
              }}
            >
              <h4 className="mk-label">Questions to ask</h4>
              <ul className="mk-stack" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {p.questions.map((q) => (
                  <li
                    key={q}
                    style={{
                      display: "flex",
                      gap: 14,
                      alignItems: "flex-start",
                      padding: "14px 0",
                      borderBottom: "1px solid var(--mk-border)",
                      font: "500 var(--mk-text-label)/1.3 var(--mk-sans)",
                      textWrap: "pretty",
                    }}
                  >
                    <CircleHelp
                      size={20}
                      color="var(--mk-primary)"
                      aria-hidden="true"
                      style={{ flex: "none", marginTop: 1 }}
                    />
                    {q}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const ForAgentsPage = () => (
  <MarketingLayout active="agents">
    <CanonicalTags
      path="/agents"
      title="For Agents | Compare Teams & Brokerages | Compaire"
      description="Find a real estate team or brokerage that fits how you work. Compare culture, leadership, training and support. Free for agents."
    />

    <section style={{ padding: "clamp(64px,10cqi,144px) 0 clamp(56px,7cqi,96px)" }}>
      <div className="mk-container mk-stack" style={{ alignItems: "flex-start", gap: 28 }}>
        <div className="mk-label">For Agents</div>
        <h1 className="mk-h1" style={{ maxWidth: 980 }}>
          Find a team or brokerage that fits <em className="mk-em">the way you work.</em>
        </h1>
        <p className="mk-lede" style={{ maxWidth: 600 }}>
          Your choice shapes your business, your daily work and life outside work. Get a clearer picture of the culture,
          leadership and support before deciding what's next.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: "16px 24px", flexWrap: "wrap" }}>
          <SignUpCta size="lg">Explore Your Options</SignUpCta>
          <div className="mk-meta-row">
            <span>100% Free &amp; Anonymous</span>
            <span aria-hidden="true">·</span>
            <span>Compare Culture &amp; Leadership</span>
          </div>
        </div>
      </div>
    </section>

    <section className="mk-section">
      <div className="mk-container mk-stack" style={{ gap: "clamp(40px,5cqi,64px)" }}>
        <SectionHead
          label="Why You're Here"
          title="What matters in your next team or brokerage?"
          body="A recruiting conversation is easier when you know what you want to ask."
        />
        <div
          className="mk-card"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,300px),1fr))",
            overflow: "hidden",
          }}
        >
          {PROBLEMS.map((item) => (
            <div
              key={item.title}
              className="mk-stack"
              style={{
                padding: "clamp(28px,3cqi,40px)",
                gap: 14,
                borderRight: "1px solid var(--mk-border)",
                borderBottom: "1px solid var(--mk-border)",
                margin: "0 -1px -1px 0",
              }}
            >
              <h3 style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>{item.title}</h3>
              <p className="mk-body">{item.body}</p>
            </div>
          ))}
        </div>
        <p style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)", color: "var(--mk-primary)" }}>
          You don't have to be ready to move to start asking these questions.
        </p>
      </div>
    </section>

    <Framework />

    <section className="mk-section mk-section--rule">
      <div
        className="mk-container"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,440px),1fr))",
          gap: "clamp(40px,6cqi,96px)",
          alignItems: "center",
        }}
      >
        <div className="mk-stack" style={{ alignItems: "flex-start", gap: 20, maxWidth: 520 }}>
          <div className="mk-label">Fit Score</div>
          <h2 className="mk-h2">See how your preferences line up.</h2>
          <p className="mk-body" style={{ textWrap: "pretty" }}>
            Your Fit Score compares your preferences with available team and brokerage information and review
            ratings, helping you choose which options to explore.
          </p>
          <SignUpCta style={{ marginTop: 8 }}>Get My Fit Score</SignUpCta>
        </div>
        <div
          role="figure"
          aria-label="Illustrative Fit Score sample"
          className="mk-card"
          style={{
            boxShadow: "0 1px 2px rgba(28,24,19,0.04),0 24px 48px -28px rgba(28,24,19,0.2)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 12,
              padding: "18px 24px",
              borderBottom: "1px solid var(--mk-border)",
            }}
          >
            <div className="mk-stack" style={{ gap: 6 }}>
              <span style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>Fit Score</span>
              <span className="mk-sample">Maverick Real Estate · Roseville, CA</span>
            </div>
            <span style={{ font: "500 var(--mk-text-caption)/1.3 var(--mk-sans)", color: "var(--mk-muted)", textAlign: "right" }}>
              Illustrative sample — not a live result.
            </span>
          </div>
          <div
            style={{
              padding: 24,
              display: "flex",
              gap: 24,
              alignItems: "center",
              flexWrap: "wrap",
              borderBottom: "1px solid var(--mk-hair)",
            }}
          >
            <div className="mk-stack" style={{ alignItems: "center", gap: 10 }}>
              <span className="mk-top-match" style={{ fontSize: "var(--mk-text-caption)", padding: "4px 6px", gap: 5 }}>
                <span className="mk-dot" aria-hidden="true" />
                Top Match
              </span>
              <div
                aria-hidden="true"
                style={{
                  width: 112,
                  height: 112,
                  borderRadius: "50%",
                  background: "conic-gradient(var(--mk-primary) 0 94%, var(--mk-tint) 94% 100%)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <div
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: "50%",
                    background: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 4,
                  }}
                >
                  <span style={{ font: "700 var(--mk-text-stat)/1.05 var(--mk-sans)", color: "var(--mk-primary)" }}>94</span>
                  <span style={{ font: "500 var(--mk-text-caption)/1 var(--mk-sans)", color: "var(--mk-muted)" }}>Fit Score</span>
                </div>
              </div>
            </div>
            <div className="mk-stack" style={{ gap: 6, minWidth: 0 }}>
              <span className="mk-sr-only">Fit Score 94 for</span>
              <span style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>Maverick Real Estate</span>
              <span style={{ font: "400 var(--mk-text-small)/1.4 var(--mk-sans)", color: "var(--mk-muted)" }}>Team · Roseville, CA</span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  font: "500 var(--mk-text-caption)/1 var(--mk-sans)",
                  marginTop: 4,
                }}
              >
                <span className="mk-dot" aria-hidden="true" />
                Top Match
              </span>
            </div>
          </div>
          <div className="mk-stack" style={{ padding: "20px 24px 24px", gap: 12 }}>
            <span style={{ font: "500 var(--mk-text-caption)/1.3 var(--mk-sans)", color: "var(--mk-muted)" }}>
              Uses the following information, where available and comparable
            </span>
            <div className="mk-chip-row">
              {["Compensation model", "Lead support", "Training", "Benefits", "Review ratings"].map((factor) => (
                <span key={factor} className="mk-chip mk-chip--sm">
                  {factor}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="how-it-works" className="mk-section mk-section--white mk-section--rule" style={{ scrollMarginTop: 96 }}>
      <div className="mk-container mk-stack" style={{ gap: "clamp(40px,5cqi,64px)" }}>
        <SectionHead label="How It Works" title="Find your perfect fit in 3 simple steps." maxWidth={640} />
        <ol className="mk-steps">
          {STEPS.map((step, i) => (
            <li key={step.title}>
              <span className="mk-steps__num">0{i + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </li>
          ))}
        </ol>
        <div style={{ display: "flex", alignItems: "center", gap: "16px 24px", flexWrap: "wrap", paddingTop: 8 }}>
          <SignUpCta>Get Started Free</SignUpCta>
          <div className="mk-meta-row">
            <span>No credit card required</span>
            <span aria-hidden="true">·</span>
            <span>100% anonymous</span>
            <span aria-hidden="true">·</span>
            <span>Free forever</span>
          </div>
        </div>
      </div>
    </section>

    <CtaBand
      title={
        <>
          Your next step starts with <em>a clearer picture.</em>
        </>
      }
      body="Explore at your own pace. Save options, compare what matters and reach out when you're ready."
      cta={
        <SignUpCta variant="inverse" size="lg" style={{ padding: "0 26px" }}>
          Find Your Fit →
        </SignUpCta>
      }
    />
  </MarketingLayout>
);

export default ForAgentsPage;
